<template lang='pug'>
  .product-call-to-action(:class='ctaClasses')
    template(v-if="isEarlyAccessVariantIneligible && ['wishlist', 'waitlist'].includes($route.name)")
      EarlyAccessCta(
        :variant='variant'
        :btnClasses='btnClasses'
        :isSmallButton='true'
        :product='product'
      )
    template(v-else)
      template(v-if='showRemindMe')
        template(v-if='!!currentUser')
          template(v-if='isAppExclusive')
            button.btn.btn-block.product-card-remind-me.disabled(
              :class='btnClasses'
              v-t='"buttons.appExclusive"'
            )
          template(v-else-if='isEarlyAccessNotificationSubscribed')
            button.btn.btn-block.product-card-remind-me.disabled(
              :class='btnClasses'
              v-t='"buttons.remindMe"'
            )
          template(v-else)
            button.btn.btn-block.product-card-remind-me(
              :class='btnClasses'
              @click='remindMe'
              v-t='"buttons.remindMe"'
            )
        template(v-else)
          router-link.btn.btn-block.product-card-remind-me(
            :class='btnClasses'
            :to='earlyAccessNotificationSubcriptionLinkPayload'
            v-t='"buttons.remindMe"'
            append
            replace
          )
      template(v-else-if='outOfStock')
        template(v-if='!!currentUser')
          template(v-if='isWaitlisted')
            button.btn.btn-block.product-card-add-to-wait-list.disabled(
              :class='btnClasses'
              v-t='"buttons.addedToWaitlist"'
            )
          template(v-else)
            button.btn.btn-block.product-card-add-to-wait-list(
              :class='btnClasses'
              @click='addToWaitList'
              v-t='"buttons.addToWaitlist"'
            )

        template(v-else)
          router-link.btn.btn-block.product-card-add-to-wait-list(
            :class='btnClasses'
            :to='{ query: { ...$router.currentRoute.query, waitlist: this.variant.id }, hash: "authentication" }'
            v-t='"buttons.addToWaitlist"'
            append
            replace
          )

      template(v-else)
        template(v-if='isAddToBagPending')
          button.btn.btn-block.btn-primary.product-card-add-to-bag.disabled(
            :class='btnClasses'
            v-t='"buttons.adding"'
          )
        template(v-else)
          button.btn.btn-block.btn-primary.product-card-add-to-bag(
            :class='btnClasses'
            @click='productShowRoute ? redirectToPdp() : addToBag()'
            v-text='addToBagText'
          )
</template>

<script>
import get from 'lodash/get'
import { mapState } from 'vuex'
import { notifyBugsnag } from '@/modules/services/bugsnag'
import { pushDataLayerEvent } from '@/modules/services/gtm'
import { humanizePrice, centsToDollars } from '@/modules/services/price-support'
import VisibilityObserver from '@/modules/services/visibility-observer'
import EarlyAccessCta from '@/components/products/show/early-access-cta.vue'
import { variantUtil } from '@/mixins/variantUtil'

export default {
  components: {
    EarlyAccessCta
  },

  mixins: [
    variantUtil
  ],

  props: {
    type: {
      type: String,
      default: ''
    },
    product: {
      type: Object,
      default: () => {}
    },
    variant: {
      type: Object,
      default: () => {}
    },
    size: {
      type: String,
      default: ''
    },
    addToBagText: {
      type: String,
      default () { return this.$t('buttons.addToBag') }
    },
    isRetailOrder: {
      type: Boolean,
      default: false
    },
    isFullSizeAvailable: {
      type: Boolean,
      default: () => false
    },
    fullSizeProductId: {
      type: Number,
      default: null
    },
    isSampleLineItem: {
      type: Boolean,
      default: false
    },
    gaOptions: {
      type: Object,
      default: () => {}
    },
    productShowRoute: {
      type: Object,
      default: () => {}
    },
    isSmallButton: {
      type: Boolean,
      default: false
    },
    showRemindMe: {
      type: Boolean,
      default: false
    },
    isAppExclusive: {
      type: Boolean,
      default: false
    },
    earlyAccessNotificationEventDetails: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      observer: this.visibilityObserver(),
      isAddToBagPending: false,
      isWaitlisted: false,
      isEarlyAccessNotificationSubscribed: false
    }
  },

  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
      waitlist: state => state.waitlist.waitlist,
      earlyAccessNotificationSubscriptions: state => state.earlyAccessNotification.earlyAccessNotificationSubscriptions,
      currentQuantity: state => state.product?.currentQuantity || 1,
      fullSizeEnabled: () => vueSupport?.featureFlags?.enableFullSize
    }),

    isMobile: () => vueSupport.isMobile,

    ctaClasses () {
      return {
        'without-quantity': this.size !== 'lg' || this.variant.attributes['sold-out'] || this.isEarlyAccessVariantIneligible,
        'with-swatch': this.isMobile && get(this.product, 'attributes["variants-count"]', 0) > 1
      }
    },

    showNewBtn () {
      return this.$route.path.includes('orders')
    },

    btnClasses () {
      let classes = this.fullSizeEnabled && this.showNewBtn ? 'new-btn ' : ''
      classes += this.isFullSizeAvailable ? 'black-btn ' : ''
      return this.size.length > 0 ? `${classes}btn-${this.size}` : classes
    },

    outOfStock () {
      return !(this.isFullSizeAvailable || !this.variant.attributes['sold-out'])
    },

    earlyAccessNotificationSubcriptionLinkPayload () {
      const launchConfig = this.variant.attributes['launch-config']

      let query = {
        ...this.$router.currentRoute.query,
        remindMeVariantId: this.variant.id
      }

      if (launchConfig) {
        query = {
          ...query,
          tiers: launchConfig.prelaunch?.tiers,
          prelaunchName: launchConfig.prelaunch?.name,
          launchName: launchConfig.launch?.name
        }
      }

      return {
        query,
        hash: 'authentication'
      }
    }
  },

  watch: {
    variant () {
      this.setWaitlistText()
      this.setNotificationSubscription()
    },

    waitlist () {
      this.setWaitlistText()
    },

    earlyAccessNotificationSubscriptions () {
      this.setNotificationSubscription()
    }
  },

  mounted () {
    this.setWaitlistText()
    this.setNotificationSubscription()

    if (this.observer && this.isFullSizeAvailable) {
      this.observer.observe(this.$el)
    }
  },

  destroyed () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },

  methods: {
    async addToWaitList () {
      this.isWaitlisted = true
      this.trackAddToWaitlistClick()

      try {
        await this.$store.dispatch('addVariantsToWaitlist', [this.variant.id])
      } catch (error) {
        this.isWaitlisted = false
        this.$store.commit('setNotification', {
          type: 'error',
          msg: this.$t('notifications.error')
        })
      }
    },

    async remindMe () {
      const { eventName, eventType } = this.earlyAccessNotificationEventDetails || {}

      if (!eventName || !eventType) {
        this.$store.commit('setNotification', {
          type: 'error',
          msg: this.$t('notifications.remindMeError')
        })
        return
      }

      this.isEarlyAccessNotificationSubscribed = true

      try {
        await this.$store.dispatch('addVariantsToNotificationSubscription', {
          variantId: this.variant.id,
          eventName,
          eventType
        })
      } catch (error) {
        this.isEarlyAccessNotificationSubscribed = false
        this.$store.commit('setNotification', {
          type: 'error',
          msg: this.$t('notifications.remindMeError')
        })
      }
    },

    async trackAddToWaitlistClick () {
      if (this.$route.name !== 'order-details' && this.$route.name !== 'retail-order-details') return

      pushDataLayerEvent(
        this.isRetailOrder ? 'store_purchase_add_to_waitlist' : 'online_store_add_to_waitlist',
        {
          params: {
            product_id: this.product?.id?.toString() || this.variant.attributes.productId.toString(),
            variant_id: this.variant.id.toString()
          }
        }
      )
    },

    visibilityObserver () {
      const opts = {
        intersectionRatio: 1.0,
        rootMargin: '-50px 0px 0px 0px'
      }
      return VisibilityObserver(this.recordImpressionEvent, opts)
    },

    recordImpressionEvent () {
      pushDataLayerEvent(
        'view_full_size_impression', {
          params: {
            filter_type: this.isSampleLineItem ? 'sample' : 'reward',
            product_id: this.fullSizeProductId?.toString(),
            variant_id: this.variant?.id,
            origin: this.isRetailOrder ? 'order_details_retail' : 'order_details_online'
          }
        }
      )
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    async addToBag () {
      this.isAddToBagPending = true
      const variantId = this.variant.id

      try {
        await this.$store.dispatch('addToBag', {
          variantId,
          quantity: this.currentQuantity
        })

        if (window.location.pathname.includes('/cart')) {
           window.location.reload()
        }

        if (this.type === 'wishlist') {
          this.$store.commit('setPageNumber', 1)
          await this.$store.dispatch('removeVariantsFromWishlist', [variantId])
          await this.$store.dispatch('fetchWishlistFilters')
        }

        try {
          this.updateDataLayer()
          this.notifyDataLayer()
        } catch (error) {
          notifyBugsnag(error)
        }

        if (this.isMobile) {
          this.$store.commit('setNotification', {
            msg: this.$t('notifications.addedToBag'),
            link: {
              path: '/cart',
              text: this.$t('notifications.viewBagCheckout')
            }
          })
        }
      } catch (error) {
        this.$store.commit('setNotification', {
          type: 'error',
          msg: this.$t('notifications.error')
        })
      }

      this.isAddToBagPending = false
    },

    setWaitlistText () {
      this.isWaitlisted = this.waitlist.indexOf(parseInt(this.variant.id)) !== -1
    },

    setNotificationSubscription () {
      this.isEarlyAccessNotificationSubscribed =
      this.earlyAccessNotificationSubscriptions.indexOf(
        parseInt(this.variant.id)
      ) !== -1
    },

    updateDataLayer () {
      if (!this.product || this.isRetailOrder) return

      const quantity = this.currentQuantity

      dataLayer[0].cart.products.push({
        variant_id: parseInt(this.variant.id),
        name: this.product.attributes['name'],
        brand_name: this.variant.attributes['brand-name'],
        quantity: quantity,
        total_price: centsToDollars(this.variant.attributes['price'] * quantity),
        total_price_human: humanizePrice(this.variant.attributes['price'] * quantity),
        unit_price: centsToDollars(this.variant.attributes['price']),
        unit_price_human: humanizePrice(this.variant.attributes['price']),
        currency: I18n.currency,
        image_url: this.variant.attributes['default-image-urls'][0],
        url: this.product.attributes['web-url']
      })
    },

    notifyDataLayer () {
      if (!this.product) return

      const quantity = this.currentQuantity

      if (this.isRetailOrder) {
        pushDataLayerEvent('store_purchase_re-order', {
          params: {
            product_id: this.product.id.toString(),
            variant_id: this.variant.id.toString()
          }
        })
      } else {
        pushDataLayerEvent('Add to Cart', {
          product_id: this.product.id,
          product_name: this.product.attributes['name'],
          variant_id: this.variant.id,
          brand: this.variant.attributes['brand-name'],
          price: this.variant.attributes['price'],
          quantity: quantity,
          sku: this.variant.attributes['sap-sku']
        })
        if (this.gaOptions?.targetContainer === 'recommended-products-carousel') {
          this.recordRecommendAddToCartGtmEvent()
        }
      }
    },

    recordRecommendAddToCartGtmEvent () {
      pushDataLayerEvent(
        'add_to_cart', {
          params: {
            product_id: this.product.id,
            variant_id: this.variant.id,
            ...this.gaOptions.params
          }
        }
      )
    },

    redirectToPdp () {
      this.$router.push(this.productShowRoute)
    }
  }
}
</script>

<style lang='scss' scoped>
  .product-call-to-action {
    &.retail-line {
      .btn-block {
        border-radius: 8px;
      }
    }
    @include media-breakpoint-down(sm) {
      display: inline-block;
      width: calc(100% - #{$product-quantity-selector});
      vertical-align: middle;

      &.with-swatch {
        width: calc(100% - #{$product-quantity-selector} - #{$product-variant-swatch-size});
      }

      &.without-quantity {
        width: 100%;

        &.with-swatch {
          width: calc(100% - #{$product-variant-swatch-size});
        }
      }
    }
  }

  .new-btn {
    width: 167px;
    height: 40px;
    padding: 10px 15px 12px;
    font-size: 14px;
    border-radius: 8px;
    text-transform: capitalize;
    line-height: 21px;
    margin: 23px 15px 0 15px;

    @include media-breakpoint-down(sm) {
      width: 100%;
      margin: 0;
    }

    @include media-breakpoint-only(md) {
      margin: 0 5px 0 5px;
    }
  }

  .black-btn {
    background: $black;
    border-color: $black;
  }
</style>
